import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleNotchIcon, FlowArrowIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { orpc } from '@/api/rpc/client';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

type WorkflowRunsBadgeProps = {
  inboundEmailId: string;
  hasAny: boolean;
  runCount: number;
};

export function InboundEmailWorkflowRunsBadge({
  inboundEmailId,
  hasAny,
  runCount,
}: WorkflowRunsBadgeProps) {
  const [open, setOpen] = useState(false);

  const runsQuery = useQuery({
    ...orpc.communications.emailRules.listInboundEmailWorkflowRuns.queryOptions(
      {
        input: { inboundEmailId },
      },
    ),
    enabled: open,
  });

  if (!hasAny) {
    return null;
  }

  const runs = runsQuery.data?.runs ?? [];

  function getStatusColor(status: string) {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'running':
        return 'text-blue-600';
      case 'failed':
        return 'text-red-600';
      case 'cancelled':
        return 'text-gray-500';
      default:
        return 'text-gray-600';
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Badge
          variant="secondary"
          className="cursor-pointer gap-1 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <FlowArrowIcon className="size-3" />
          {runCount}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        className="w-80"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Workflow Runs</h4>
          {runsQuery.isLoading ? (
            <div className="flex justify-center py-4">
              <CircleNotchIcon className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : runsQuery.isError ? (
            <p className="text-sm text-destructive">Failed to load runs</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No runs found</p>
          ) : (
            <ul className="max-h-60 space-y-2 overflow-y-auto">
              {runs.map((item) => (
                <li key={item.run.id} className="text-sm">
                  <Link
                    to="/workflows/$workflowDefinitionId/runs"
                    params={{
                      workflowDefinitionId: item.run.workflowDefinitionId,
                    }}
                    className="block rounded p-2 hover:bg-muted"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {item.workflowDefinitionName}
                      </span>
                      <span
                        className={cn(
                          'text-xs',
                          getStatusColor(item.run.status),
                        )}
                      >
                        {item.run.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {new Date(item.run.triggeredAt).toLocaleString()}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
